<template>
  <div v-if="visible && list.length" class="top-banner" :style="{ height: bannerHeight + 'px' }">
    <el-carousel
      class="top-banner__carousel"
      :class="{ 'top-banner__carousel--fade': campaign.animationType === 'fade' }"
      :height="bannerHeight + 'px'"
      :interval="interval"
      :direction="direction"
      :autoplay="list.length > 1"
      indicator-position="none"
      arrow="hover"
    >
      <el-carousel-item v-for="b in list" :key="b.id">
        <img :src="b.bannerImage" class="top-banner__img" @click="onClick(b)" />
      </el-carousel-item>
    </el-carousel>
    <!-- 关闭倒计时 -->
    <div v-if="showCountdownUi" class="top-banner__countdown">{{ countdownLeft }}s</div>
    <!-- 右上角关闭按钮 -->
    <div class="top-banner__close" @click="close" :title="$t('message.home.关闭广告')">×</div>
  </div>
</template>

<script>
import { getActiveBannerCampaign } from "@/api/upms/decoration";
import { navigateByLink } from "@/util/decorationNavigate";

const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: "TopBanner",
  props: {
    height: { type: Number, default: 400 },
  },
  data() {
    return {
      visible: false,
      campaign: {},
      list: [],
      countdownLeft: 0,
      autoCloseTimer: null,
    };
  },
  computed: {
    interval() {
      const i = parseInt(this.campaign && this.campaign.carouselInterval, 10);
      return i > 0 ? i : 4000;
    },
    direction() {
      return this.campaign && this.campaign.direction === "vertical" ? "vertical" : "horizontal";
    },
    bannerHeight() {
      const h = parseInt(this.campaign && this.campaign.bannerHeight, 10);
      return h > 0 ? h : this.height;
    },
    showCountdownUi() {
      return this.countdownLeft > 0 && this.campaign && this.campaign.showCountdown;
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.clearAutoClose();
  },
  methods: {
    load() {
      const userType =
        this.$store && this.$store.getters && this.$store.getters.userInfo
          ? this.$store.getters.userInfo.userType || 0
          : null;
      const params = userType !== null ? { targetUserType: userType } : {};
      getActiveBannerCampaign(params)
        .then((r) => {
          const data = (r && r.data && r.data.data) || {};
          const campaign = data.campaign;
          if (!campaign || !campaign.id) return;
          if (!this.shouldShow(campaign)) return;
          if (!this.inDailyWindow(campaign)) return;
          const banners = (data.banners || []).filter((b) => b && b.bannerImage);
          if (!banners.length) return;
          this.campaign = campaign;
          this.list = banners;
          this.visible = true;
          this.startAutoClose();
          const key = this.frequencyKey(campaign);
          if (key) localStorage.setItem(key, String(Date.now()));
        })
        .catch(() => {});
    },
    /** 频次：复用弹窗广告同款逻辑 */
    shouldShow(c) {
      const key = this.frequencyKey(c);
      if (!key) return true;
      const last = localStorage.getItem(key);
      if (!last) return true;
      const lastTs = parseInt(last, 10);
      if (isNaN(lastTs)) return true;
      const days = this.frequencyDays(c);
      if (days <= 0) return true;
      return Date.now() - lastTs >= days * DAY_MS;
    },
    frequencyKey(c) {
      if (c.displayFrequencyType === 1) return null; // 每次进入：不记录
      return "banner_campaign_shown_" + c.id;
    },
    frequencyDays(c) {
      const type = c.displayFrequencyType;
      if (type === 0) return 36500; // 永久一次
      if (type === 2) return Math.max(parseInt(c.displayFrequencyValue, 10) || 1, 1);
      return 0;
    },
    /** 每日时间窗校验（前端兜底，服务端已先过滤） */
    inDailyWindow(c) {
      const start = c.dailyStartTime;
      const end = c.dailyEndTime;
      if (!start || !end) return true;
      const now = new Date();
      const cur = now.getHours() * 60 + now.getMinutes();
      const s = this.toMinutes(start);
      const e = this.toMinutes(end);
      if (s == null || e == null) return true;
      return s <= e ? cur >= s && cur <= e : cur >= s || cur <= e;
    },
    toMinutes(hhmm) {
      const parts = String(hhmm).split(":");
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (isNaN(h) || isNaN(m)) return null;
      return h * 60 + m;
    },
    clearAutoClose() {
      if (this.autoCloseTimer) {
        clearInterval(this.autoCloseTimer);
        this.autoCloseTimer = null;
      }
      this.countdownLeft = 0;
    },
    startAutoClose() {
      this.clearAutoClose();
      const sec = parseInt(this.campaign && this.campaign.autoCloseSeconds, 10);
      if (!sec || sec <= 0) return;
      this.countdownLeft = sec;
      this.autoCloseTimer = setInterval(() => {
        this.countdownLeft--;
        if (this.countdownLeft <= 0) {
          this.clearAutoClose();
          this.visible = false;
        }
      }, 1000);
    },
    close() {
      this.clearAutoClose();
      this.visible = false;
    },
    onClick(b) {
      if (!b || !b.linkUrl) return;
      navigateByLink(this.$router, b.linkUrl);
    },
  },
};
</script>

<style scoped>
.top-banner {
  position: relative;
  width: 100%;
}
.top-banner__carousel {
  width: 100%;
}
.top-banner__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  display: block;
}
/* 淡入淡出：el-carousel 无原生 fade，用过渡近似 */
.top-banner__carousel--fade ::v-deep .el-carousel__item {
  transition: opacity 0.6s ease;
}
.top-banner__close {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 10;
  width: 28px;
  height: 28px;
  line-height: 26px;
  text-align: center;
  font-size: 18px;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 50%;
  cursor: pointer;
  user-select: none;
}
.top-banner__close:hover {
  background: rgba(0, 0, 0, 0.65);
}
.top-banner__countdown {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  min-width: 40px;
  padding: 0 10px;
  height: 26px;
  line-height: 26px;
  text-align: center;
  color: #fff;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 13px;
}
</style>
